import { useEffect, useRef } from "react";
import "./add-receipt-menu.css";

export interface AddReceiptMenuProps {
  open: boolean;
  /** Dismiss without picking (scrim / Esc / «Скасувати»). */
  onClose: () => void;
  /** Existing photo flow (US-06) — listed first as the common path. */
  onPickPhoto: () => void;
  /** Manual create screen (ADR-0031, US-20). */
  onPickManual: () => void;
}

/**
 * The two-way add-receipt chooser (AC-20.1, components.md §6.8). The
 * add-receipt affordance no longer jumps straight to the camera — it opens
 * this menu offering «Сфотографувати чек» (photo, first) vs «Додати вручну»
 * (manual). One component, presented as a bottom sheet <768 px and a
 * bottom-right popover ≥768 px (CSS-driven, same markup). Not used on the
 * zero-receipt empty state — there the two paths render as explicit buttons.
 *
 * A11y: role="dialog" + aria-modal, focus moves to the first option on open,
 * Tab is trapped, Esc / scrim / «Скасувати» dismiss, focus restores to the
 * opener. Each option row is a <button> whose accessible name includes its
 * subtitle; icons are decorative.
 */
export function AddReceiptMenu({
  open,
  onClose,
  onPickPhoto,
  onPickManual,
}: AddReceiptMenuProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const firstRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    openerRef.current = document.activeElement as HTMLElement | null;
    firstRef.current?.focus();
    return () => {
      openerRef.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusables = panel.querySelectorAll<HTMLElement>("button");
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <div className="wp-arm" data-testid="add-receipt-menu">
      <div className="wp-arm__scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        className="wp-arm__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Додати чек"
        onKeyDown={onKeyDown}
      >
        <span aria-hidden="true" className="wp-arm__handle" />
        <p className="wp-arm__title">Додати чек</p>

        <button
          ref={firstRef}
          type="button"
          className="wp-arm__option"
          onClick={onPickPhoto}
          data-testid="add-receipt-photo"
        >
          <span aria-hidden="true" className="wp-arm__icon">
            📷
          </span>
          <span className="wp-arm__text">
            <span className="wp-arm__option-title">Сфотографувати чек</span>
            <span className="wp-arm__option-sub">AI розбере фото на позиції</span>
          </span>
          <span aria-hidden="true" className="wp-arm__chevron">
            ›
          </span>
        </button>

        <button
          type="button"
          className="wp-arm__option"
          onClick={onPickManual}
          data-testid="add-receipt-manual"
        >
          <span aria-hidden="true" className="wp-arm__icon">
            ✎
          </span>
          <span className="wp-arm__text">
            <span className="wp-arm__option-title">Додати вручну</span>
            <span className="wp-arm__option-sub">Готівка та інші витрати без чека</span>
          </span>
          <span aria-hidden="true" className="wp-arm__chevron">
            ›
          </span>
        </button>

        <button
          type="button"
          className="wp-arm__cancel"
          onClick={onClose}
          data-testid="add-receipt-cancel"
        >
          Скасувати
        </button>
      </div>
    </div>
  );
}
